/**
 * Drawing Control — screen 06, ported from the validated mockup.
 *
 * Ubi: *"It needs to be one page … he has on one page the whole list. He can search …
 * it needs to be a tick box."* Sticky header, everything on one screen, ticks that save
 * immediately.
 *
 * ⚠ This is the only screen in the product that CHANGES what an outside company sees.
 * Every other screen is a read. Two consequences run through the file:
 *
 *   1. The headline figure is "N orders open to contractors", not a count of
 *      drawings. Drawings are the control; orders are the thing people argue about.
 *   2. Bulk actions confirm with their IMPACT first — "publishes 133 drawings, 2 487
 *      orders become open" — because a bulk publish under the default All filter
 *      hands over the entire backlog in one click. That follows the language the
 *      validated company screen already uses: *"are you sure?" is not a useful question;
 *      "this removes 6 people and 1 421 rows of history" is.* Single ticks save
 *      immediately, exactly as the mockup does.
 *
 * ⚠ A drawing is published to an AUDIENCE, so the mockup's single tick per row is now
 * one tick PER CONTRACTOR. Ubi, 17 Sep 2026: *"both subcos at the same time please,
 * because some areas were re-assigned between the two, so there is still a backlog of
 * overlapping orders."* Ticking both opens the drawing to each of them — and since
 * 26 Oct 2026 each sees only the orders DMP assigns to them, never the other's (Hugues).
 * See `visibleRows`.
 *
 * ⚠ OPEN, NOT VISIBLE, SINCE 13 OCT 2026. A contractor now sees their OWN orders in a
 * drawing that is not open to them, flagged Hidden. So hiding takes orders out of a
 * contractor's open backlog rather than out of their sight, and every figure here counts
 * OPEN orders: "Circet gets 131 more open orders", never "sees 131 more" — orders they
 * could already see would otherwise be announced as new.
 *
 * ⚠ Merged drawings are GROUPED and published as one unit. Ubi, feedback of 28 Sep 2026:
 * *"ability to create POP groups based on which drawings have been merged: group name is
 * POPname-B001 (highlighted yellow) to list all drawings underneath (highlighted grey)"*.
 * The head row is tinted and carries the ticks; its members sit indented beneath it and
 * follow it. Every preview on this screen widens to whole groups with the same
 * `planAudienceChange` the server applies, so a figure here never under-states a click.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import {
  dateOnly,
  filterGroups,
  formatDayMonthYear,
  formatNumber,
  groupHeadOf,
  groupingProblem,
  groupMembersOf,
  impactOfChanges,
  isDrawingOpen,
  isIrregularDrawingName,
  ordersVisibleTo,
  planAudienceChange,
  type AudienceImpact,
  type AudienceMode,
  type DrawingRow,
  type DrawingShow,
  type DrawingTotals,
  type PopGroup,
} from "@cocon-stats/shared";
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  CornerDownRight,
  Eye,
  EyeOff,
  Inbox,
  Layers,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { ApiError, apiFetch } from "@/lib/api-client";

interface AudienceCompany {
  id: string;
  code: string;
  name: string;
  colour: string;
  /** The DMP contractor codes this company owns — what the impact figures count. */
  dmpCodes: string[];
  dmpOrders: number;
}

interface BoardResponse {
  groups: PopGroup[];
  totals: DrawingTotals;
  companies: AudienceCompany[];
  snapshot: { id: string; runDate: string; refreshedAt: string } | null;
}

interface PatchResponse extends BoardResponse {
  updated: string[];
  unknown: string[];
  /** Drawings the write reached only because they are grouped with a named one. */
  widenedBy: string[];
  /** Per company, gains and losses kept apart — see `audienceImpact` in shared. */
  impact: AudienceImpact;
}

interface GroupResponse extends BoardResponse {
  head: string;
  members: string[];
  impact: AudienceImpact;
}

interface UngroupResponse extends BoardResponse {
  released: string[];
  dissolved: string[];
}

/** A group being composed on one POP card: which POP, and which drawings are ticked. */
interface GroupingDraft {
  pop: string;
  names: string[];
}

/** The head's tint and a member's, for Ubi's yellow and grey. Inline: theme.css is shared
 *  byte for byte with the mockups, and this screen's mockup predates groups. */
const HEAD_ROW_STYLE = { background: "#fffbeb", boxShadow: "inset 3px 0 0 #f59e0b" } as const;
const MEMBER_ROW_STYLE = { background: "#f8fafc", boxShadow: "inset 3px 0 0 #e2e8f0" } as const;

/** The age pill tone, exactly as `CS.ageTone` in the mockups. */
function ageTone(days: number): string {
  if (days > 365) return "red";
  if (days > 90) return "orange";
  if (days > 30) return "amber";
  return "slate";
}

const SHOW_OPTIONS: Array<{ key: DrawingShow; label: string }> = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "hidden", label: "Hidden" },
  { key: "new", label: "New" },
  { key: "multi", label: "Multi-version" },
];

export function DrawingControl() {
  const [board, setBoard] = useState<BoardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const [show, setShow] = useState<DrawingShow>("all");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  /**
   * Which bulk action is being composed, if any.
   *
   * ⚠ Held as a MODE rather than fired on click, because the earlier version gave no
   * feedback at all: four buttons labelled "Shown → Circet" that did nothing visible
   * except open a panel further down the page. Nobody could tell what one would do, or
   * that they had pressed it. Now one button opens a panel, the button shows it is
   * pressed, and the audience is chosen where the consequence is written.
   */
  const [bulkMode, setBulkMode] = useState<"publish" | "hide" | null>(null);
  const [bulkCompanyIds, setBulkCompanyIds] = useState<string[]>([]);
  const [grouping, setGrouping] = useState<GroupingDraft | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<BoardResponse>("/admin/drawings")
      .then((data) => {
        if (cancelled) return;
        setBoard(data);
        // Multi-version POPs start expanded: they are the clash cases Ubi works, and a
        // page of collapsed rows would hide the only thing she came here to do.
        const open: Record<string, boolean> = {};
        for (const group of data.groups) open[group.key] = group.versions.length > 1;
        setExpanded(open);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The "Saved" flash, on a timer rather than a CSS animation so a second save restarts it.
  useEffect(() => {
    if (savedAt === null) return;
    const timer = setTimeout(() => setSavedAt(null), 2400);
    return () => clearTimeout(timer);
  }, [savedAt]);

  const visible = useMemo(
    () => (board ? filterGroups(board.groups, { show, search: query }) : []),
    [board, show, query],
  );

  /** Every drawing the current filter and search are showing. What "Shown" means. */
  const shownVersions = useMemo(() => visible.flatMap((g) => g.versions), [visible]);

  /**
   * Every drawing on the board, whatever the filter shows.
   *
   * ⚠ Group widening and each head's member list are computed over THIS, never over the
   * filtered view: a filter showing half a group must not make the other half vanish from
   * the preview of a write that will land on it.
   */
  const allVersions = useMemo(() => (board ? board.groups.flatMap((g) => g.versions) : []), [board]);
  const membersByHead = useMemo(() => groupMembersOf(allVersions), [allVersions]);

  /** Take a write's response as the new board, keeping what the response leaves out. */
  const adopt = useCallback(
    (result: BoardResponse) =>
      setBoard((current) => ({
        groups: result.groups,
        totals: result.totals,
        companies: result.companies ?? current?.companies ?? [],
        snapshot: current?.snapshot ?? null,
      })),
    [],
  );

  /**
   * ⚠ Named, per company, in both directions. "1 421 orders are now open to
   * contractors" does not say WHICH contractor, and after a change that gives work to
   * one and takes it from the other it is not even true.
   */
  const reportImpact = useCallback(
    (impact: AudienceImpact, companies: AudienceCompany[]) => {
      const nameOf = (id: string) => companies.find((c) => c.id === id)?.name ?? "a contractor";
      const phrases = (entries: Record<string, number>, verb: string, noun: string) =>
        Object.entries(entries).map(([id, n]) => `${nameOf(id)} ${verb} ${formatNumber(n)} ${noun}`);
      const gained = phrases(impact.gains, "gets", "more open orders");
      const lost = phrases(impact.losses, "loses", "open orders");
      if (gained.length > 0) toast.success(`${gained.join(", ")}.`);
      if (lost.length > 0) toast.success(`${lost.join(", ")}.`);
    },
    [],
  );

  const apply = useCallback(async (projectnames: string[], mode: AudienceMode, companyIds: string[]) => {
    if (projectnames.length === 0) return;
    setSaving(true);
    try {
      const result = await apiFetch<PatchResponse>("/admin/drawings", {
        method: "PATCH",
        body: JSON.stringify({ projectnames, mode, companyIds }),
      });
      adopt(result);
      setSavedAt(Date.now());
      reportImpact(result.impact, result.companies ?? board?.companies ?? []);
      if (result.widenedBy.length > 0) {
        // ⚠ Said out loud: a tick on one row changed several. That is the group working,
        // and it must never be discovered afterwards.
        toast.info(
          `Applied to the whole group — ${result.widenedBy.length} grouped drawing${
            result.widenedBy.length === 1 ? "" : "s"
          } followed.`,
        );
      }
      if (result.unknown.length > 0) {
        // Never silent: a name the server did not recognise means the list on screen is
        // out of step with the database, and acting on a stale list is the one way to
        // publish something nobody chose.
        toast.warning(`${result.unknown.length} drawing(s) no longer exist — reload the page.`);
      }
    } catch (err) {
      // A refusal is a sentence worth reading — "Walfit has no DMP contractor code…".
      toast.error(err instanceof ApiError && err.status === 400 ? err.message : "The change could not be saved. Nothing was opened or hidden.");
    } finally {
      setSaving(false);
    }
  }, [adopt, reportImpact, board?.companies]);

  const createGroup = useCallback(
    async (projectnames: string[]) => {
      setSaving(true);
      try {
        const result = await apiFetch<GroupResponse>("/admin/drawings/groups", {
          method: "POST",
          body: JSON.stringify({ projectnames }),
        });
        adopt(result);
        setSavedAt(Date.now());
        setGrouping(null);
        toast.success(
          `${result.members.length} drawing${result.members.length === 1 ? "" : "s"} grouped under ${result.head}.`,
        );
        reportImpact(result.impact, result.companies ?? board?.companies ?? []);
      } catch (err) {
        toast.error(
          err instanceof ApiError && err.status === 400 ? err.message : "The group could not be created. Nothing changed.",
        );
      } finally {
        setSaving(false);
      }
    },
    [adopt, reportImpact, board?.companies],
  );

  const ungroup = useCallback(
    async (projectname: string, isHead: boolean) => {
      setSaving(true);
      try {
        const result = await apiFetch<UngroupResponse>("/admin/drawings/ungroup", {
          method: "POST",
          body: JSON.stringify({ projectnames: [projectname] }),
        });
        adopt(result);
        setSavedAt(Date.now());
        toast.success(
          isHead
            ? `Group ${projectname} dissolved. Every drawing keeps the audience it has now.`
            : `${projectname} is no longer grouped. It keeps the audience it has now.`,
        );
      } catch {
        toast.error("The group could not be changed. Nothing changed.");
      } finally {
        setSaving(false);
      }
    },
    [adopt],
  );

  /**
   * Add or remove one company from one drawing's audience.
   *
   * ⚠ `set` is correct HERE and nowhere else in this file: this is one drawing, and its
   * entire audience is on screen beside the tick being clicked, so naming the whole
   * resulting audience cannot surprise anybody. The bulk actions use add/remove.
   */
  const toggleCompany = useCallback(
    (version: DrawingRow, companyId: string) => {
      // On a group head this lands on the whole group — the server widens it, and the
      // head row says "Group of N" right beside the tick.
      const next = version.openToCompanyIds.includes(companyId)
        ? version.openToCompanyIds.filter((id) => id !== companyId)
        : [...version.openToCompanyIds, companyId];
      void apply([version.projectname], "set", next);
    },
    [apply],
  );

  /**
   * ⚠ A bulk action NEVER sets an audience outright — it adds to it or takes from it.
   *
   * Hugues, 17 Sep 2026: *"the ability to select for which subco we want to hide (now it
   * hides for both de facto) while when publishing we can select which subco(s)"*. The
   * intention is about a company, but the drawings it lands on are in different states,
   * so there is no single target audience that expresses it. Setting one broke BOTH
   * directions: "hide" hid from everybody, and "publish to Circet" would have quietly
   * withdrawn Constructel from every drawing the two were sharing.
   */
  const bulkAudienceMode: AudienceMode = bulkMode === "hide" ? "remove" : "add";

  /**
   * What the composed action would actually do, recomputed as companies are ticked.
   *
   * ⚠ The SAME function the server applies, counting only rows the audience would really
   * see. The version before this said "up to 2 507 orders" for publishing the whole
   * backlog to Constructel, when 1 066 of those rows are theirs and 20 belong to nobody —
   * overstating by more than double on the one screen whose job is to say exactly what is
   * being handed to an outside company.
   *
   * ⚠ Widened to whole groups first, exactly as the server will. A filter can show part of
   * a group, and a preview that counted only the shown part would under-state the click.
   */
  const bulkPlan = useMemo(
    () =>
      planAudienceChange(
        allVersions,
        shownVersions.map((v) => v.projectname),
        bulkAudienceMode,
        bulkCompanyIds,
      ),
    [allVersions, shownVersions, bulkAudienceMode, bulkCompanyIds],
  );
  const bulkImpact = useMemo(
    () =>
      impactOfChanges(
        bulkPlan.changes.map((c) => ({
          projectname: c.drawing.projectname,
          before: c.before,
          after: c.after,
          ordersByContractor: c.drawing.ordersByContractor,
        })),
        board?.companies ?? [],
      ),
    [bulkPlan, board?.companies],
  );
  /** Drawings the bulk action changes only because they are grouped with shown ones. */
  const bulkWidened = useMemo(
    () => bulkPlan.widenedBy.filter((name) => bulkImpact.names.includes(name)),
    [bulkPlan, bulkImpact],
  );

  /** How many of the drawings shown hold orders of this company's own. */
  const ordersOwnedInView = useCallback(
    (company: AudienceCompany) =>
      shownVersions.reduce((sum, v) => sum + ordersVisibleTo(v, company.dmpCodes), 0),
    [shownVersions],
  );

  /** How many of this company's orders are open to it RIGHT NOW among the drawings shown. */
  const ordersSeenInView = useCallback(
    (company: AudienceCompany) =>
      shownVersions.reduce(
        (sum, v) => sum + (v.openToCompanyIds.includes(company.id) ? ordersVisibleTo(v, company.dmpCodes) : 0),
        0,
      ),
    [shownVersions],
  );

  const closeBulk = useCallback(() => {
    setBulkMode(null);
    setBulkCompanyIds([]);
  }, []);

  if (loading) {
    return <div className="px-4 py-6 text-sm text-slate-400 lg:px-8">Loading…</div>;
  }

  if (failed || !board) {
    return (
      <div className="px-4 py-6 lg:px-8">
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
          <h3 className="font-semibold text-slate-700">Drawing Control could not be loaded</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">Reload the page, or try again shortly.</p>
        </div>
      </div>
    );
  }

  const t = board.totals;

  return (
    <div className="max-w-[1400px] px-4 py-6 lg:px-8">
      {/* Sticky control header — stays put while the list scrolls */}
      <div
        className="sticky z-10 -mx-4 space-y-3 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur lg:-mx-8 lg:px-8"
        style={{ top: "4rem" }}
      >
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <div className="flex items-baseline gap-2">
            <span className="num text-2xl font-bold text-slate-900">{formatNumber(t.drawings)}</span>
            <span className="text-sm text-slate-500">
              drawings across <span className="num font-semibold text-slate-700">{t.pops}</span> POPs
            </span>
          </div>
          <span className="pill pill-emerald">
            <span className="num">{t.open}</span>&nbsp;Open
          </span>
          <span className="pill pill-slate">
            <span className="num">{t.hidden}</span>&nbsp;Hidden
          </span>
          <span className="pill pill-rose">
            <span className="num">{t.neverDecided}</span>&nbsp;New
          </span>
          {savedAt !== null && (
            <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: "#047857" }}>
              <CheckCircle className="h-3.5 w-3.5" /> Saved
            </span>
          )}
          <span className="ml-auto text-xs text-slate-500">
            <span className="num font-semibold text-slate-700">
              {formatNumber(t.ordersVisibleToContractors)}
            </span>{" "}
            orders open to contractors
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* ⚠ 230 px is what "Search POP or drawing" needs with its icon. At 200 the
              toolbar squeezed the box once Collapse All joined it, and the placeholder read
              "Search POP or drawir". Below that the buttons wrap to a line of their own. */}
          <div className="relative min-w-[230px] max-w-sm flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search className="h-4 w-4" />
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (e.target.value.trim()) expandAll(board.groups, setExpanded);
              }}
              placeholder="Search POP or drawing"
              className="field field-icon"
            />
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <span className="eyebrow mr-1">Show</span>
            {SHOW_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`chip${show === option.key ? " chip-active" : ""}`}
                onClick={() => {
                  setShow(option.key);
                  if (option.key !== "all") expandAll(board.groups, setExpanded);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {/* ⚠ One button, and it says what it acts on. The earlier four — "Shown → Circet",
                "Shown → Constructel", "Shown → both" — were jargon nobody could decode, gave
                no pressed state, and hid the audience choice from the sentence that states
                the consequence. */}
            <button
              type="button"
              className={`btn btn-sm ${bulkMode === "publish" ? "gradient-btn" : "btn-outline"}`}
              disabled={saving}
              onClick={() => {
                setBulkMode(bulkMode === "publish" ? null : "publish");
                setBulkCompanyIds([]);
              }}
            >
              <Eye className="h-3.5 w-3.5" /> Open all {formatNumber(shownVersions.length)} shown…
            </button>
            <button
              type="button"
              className={`btn btn-sm ${bulkMode === "hide" ? "gradient-btn" : "btn-outline"}`}
              disabled={saving}
              onClick={() => {
                const next = bulkMode === "hide" ? null : "hide";
                setBulkMode(next);
                // ⚠ Hide starts with EVERY contractor ticked, publish with none. The safe
                // default is whichever one shows less: pre-ticking a publish would be one
                // click away from handing over a backlog, while pre-ticking a hide only
                // ever withdraws — and it keeps "hide all of this" a single click, which
                // is what the button did before it could be narrowed to one company.
                setBulkCompanyIds(next === "hide" ? board.companies.map((c) => c.id) : []);
              }}
            >
              <EyeOff className="h-3.5 w-3.5" /> Hide all {formatNumber(shownVersions.length)} shown…
            </button>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => expandAll(board.groups, setExpanded)}
            >
              Expand All
            </button>
            {/* Ubi, Sep 2026: *"Collapse all"*. Every POP back to one line — including the
                multi-version ones that open expanded on load. */}
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setExpanded({})}>
              Collapse All
            </button>
          </div>
        </div>
      </div>

      {/*
        ⚠ Not in the mockup, and deliberate. A bulk publish under the default All filter
        hands the whole backlog to a contractor from one click, so it states its impact and
        waits — and the audience is chosen HERE, inside the sentence that says what happens,
        rather than encoded in a button label. A single tick on a row still saves
        immediately: that is one drawing, and it is the action Ubi came here to perform.
      */}
      {bulkMode && (
        <div
          className="card mt-5 p-4"
          style={{ borderLeft: `4px solid ${bulkMode === "publish" ? "#ed125f" : "#64748b"}` }}
        >
          <p className="text-sm font-semibold text-slate-800">
            {bulkMode === "publish" ? "Open" : "Hide"} the {formatNumber(shownVersions.length)} drawings shown below
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {/* ⚠ Says what "shown" means. It is the whole filter in one sentence, and with
                the default All filter it is every drawing there is. */}
            &ldquo;Shown&rdquo; is what the filter and search above are currently listing
            {show === "all" && !query.trim() ? " — which right now is every drawing" : ""}.
          </p>

          {/* ⚠ Both directions choose companies now. Hiding used to mean hiding from
              everybody, which is wrong where both contractors work a drawing: taking it
              back from one of them should leave the other working. */}
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <span className="eyebrow">{bulkMode === "publish" ? "Open to" : "Hide from"}</span>
            {board.companies.map((company) => {
              const ticked = bulkCompanyIds.includes(company.id);
              // ⚠ Two different questions, so two different numbers. Before publishing,
              // what matters is how much of their OWN work is in these drawings; before
              // hiding, how much is open to them today and stands to close.
              const context =
                bulkMode === "publish"
                  ? { n: ordersOwnedInView(company), label: "in view" }
                  : { n: ordersSeenInView(company), label: "open now" };
              return (
                <label key={company.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="tick"
                    checked={ticked}
                    onChange={() =>
                      setBulkCompanyIds((ids) =>
                        ids.includes(company.id) ? ids.filter((id) => id !== company.id) : [...ids, company.id],
                      )
                    }
                  />
                  <span className={ticked ? "font-semibold text-slate-800" : "text-slate-500"}>
                    {company.name}
                  </span>
                  {/* ⚠ Labelled, not bare. This is context, NOT the impact figure below —
                      which is a delta — and two bare numbers side by side read as if they
                      ought to agree. */}
                  <span
                    className="num text-xs text-slate-400"
                    title={
                      bulkMode === "publish"
                        ? `${company.name} has ${context.n} orders of their own among the drawings shown`
                        : `${company.name} has ${context.n} orders open to them among the drawings shown`
                    }
                  >
                    {formatNumber(context.n)} {context.label}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="mt-3 text-sm text-slate-700">
            {bulkCompanyIds.length === 0 ? (
              <span className="text-slate-400">
                Choose who to {bulkMode === "publish" ? "open them to" : "hide them from"}.
              </span>
            ) : bulkImpact.names.length === 0 ? (
              <span className="text-slate-400">Everything shown is already in that state — nothing to do.</span>
            ) : (
              <>
                <p>
                  Changes{" "}
                  <span className="font-semibold">{formatNumber(bulkImpact.names.length)} drawings</span>
                  {bulkMode === "publish" ? (
                    <>
                      {" "}
                      —{" "}
                      {phraseImpact(board.companies, bulkImpact.gains, "gets", "more open orders")}{" "}
                      immediately.
                      {bulkCompanyIds.length > 1 && <> Each sees only the orders DMP assigns to them.</>}
                    </>
                  ) : (
                    <>
                      {" "}
                      —{" "}
                      {phraseImpact(board.companies, bulkImpact.losses, "loses", "open orders")} immediately.
                      {/* Since 13 Oct 2026, hiding closes orders; it does not take them away. */}
                      <> Their own orders stay in their report, marked Hidden.</>
                    </>
                  )}
                </p>

                {bulkWidened.length > 0 && (
                  <p className="mt-2 text-xs text-slate-500">
                    Includes {formatNumber(bulkWidened.length)} drawing{bulkWidened.length === 1 ? "" : "s"} not shown —{" "}
                    <span className="mono">{bulkWidened.slice(0, 4).join(", ")}</span>
                    {bulkWidened.length > 4 ? ` and ${bulkWidened.length - 4} more` : ""} — because{" "}
                    {bulkWidened.length === 1 ? "it is" : "they are"} grouped with drawings that are. A group is
                    opened and hidden as one unit.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2">
            <button type="button" className="btn btn-sm btn-outline" onClick={closeBulk}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm gradient-btn"
              disabled={saving || bulkCompanyIds.length === 0 || bulkImpact.names.length === 0}
              onClick={() => {
                const names = bulkImpact.names;
                const mode = bulkAudienceMode;
                const ids = bulkCompanyIds;
                closeBulk();
                void apply(names, mode, ids);
              }}
            >
              {bulkMode === "publish" ? "Open them" : "Hide them"}
            </button>
          </div>
        </div>
      )}

      {t.neverDecided > 0 && (
        <div className="card mt-5 flex flex-wrap items-center gap-3 p-4" style={{ borderLeft: "4px solid #ed125f" }}>
          <Sparkles className="h-5 w-5 shrink-0" style={{ color: "#ed125f" }} />
          <p className="min-w-[240px] flex-1 text-sm text-slate-700">
            <span className="font-semibold">
              {t.neverDecided} drawing{t.neverDecided === 1 ? "" : "s"}{" "}
              {t.neverDecided === 1 ? "has" : "have"} never been decided on.
            </span>{" "}
            They stay hidden from contractors until you open them.
          </p>
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={() => {
              setShow("new");
              expandAll(board.groups, setExpanded);
            }}
          >
            Review Them
          </button>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {visible.map((group) => (
          <PopCard
            key={group.key}
            group={group}
            allVersions={allVersions}
            membersByHead={membersByHead}
            companies={board.companies}
            expanded={expanded[group.key] ?? false}
            onToggleExpand={() => setExpanded((e) => ({ ...e, [group.key]: !e[group.key] }))}
            onToggleCompany={toggleCompany}
            grouping={grouping?.pop === group.key ? grouping : null}
            onStartGrouping={() => {
              setExpanded((e) => ({ ...e, [group.key]: true }));
              setGrouping({ pop: group.key, names: [] });
            }}
            onToggleSelected={(name) =>
              setGrouping((draft) =>
                draft
                  ? {
                      ...draft,
                      names: draft.names.includes(name)
                        ? draft.names.filter((n) => n !== name)
                        : [...draft.names, name],
                    }
                  : draft,
              )
            }
            onCancelGrouping={() => setGrouping(null)}
            onCreateGroup={(names) => void createGroup(names)}
            onUngroup={(name, isHead) => void ungroup(name, isHead)}
            saving={saving}
          />
        ))}
      </div>

      {visible.length === 0 && (
        <div className="card mt-5 py-16 text-center">
          <Inbox className="mx-auto h-6 w-6 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No drawings match this search.</p>
          <button
            type="button"
            className="mt-1 text-sm font-semibold"
            style={{ color: "#ed125f" }}
            onClick={() => {
              setQuery("");
              setShow("all");
            }}
          >
            Clear the search
          </button>
        </div>
      )}

      <p className="mt-6 text-xs text-slate-400">
        A hidden drawing removes its orders from every contractor view immediately — it changes nothing in DMP or
        COCON.{" "}
        {board.snapshot ? (
          <>
            The drawings and their counts come from the snapshot of{" "}
            <span className="font-medium text-slate-500">{formatDayMonthYear(dateOnly(board.snapshot.runDate))}</span>:
            a drawing that appeared in DMP after it is listed from the next snapshot on.
          </>
        ) : (
          "No snapshot has run yet, so no drawing is known."
        )}
      </p>
    </div>
  );
}

/**
 * "Circet gets 131 more open orders and Constructel gets 40 more open orders."
 *
 * ⚠ One clause per company, never one total. A single click can widen one contractor's
 * view and narrow the other's, and a netted figure would report that as almost nothing
 * happening — the hedge hard rule 24 exists to stop.
 */
function phraseImpact(
  companies: AudienceCompany[],
  entries: Record<string, number>,
  verb: string,
  noun: string,
): ReactNode {
  const named = companies.filter((c) => (entries[c.id] ?? 0) > 0);
  if (named.length === 0) return <span className="text-slate-500">nothing changes hands</span>;
  return (
    <>
      {named.map((company, i) => (
        <span key={company.id}>
          {i > 0 ? " and " : ""}
          <span className="font-semibold">{company.name}</span> {verb}{" "}
          <span className="font-semibold">
            {formatNumber(entries[company.id] ?? 0)} {noun}
          </span>
        </span>
      ))}
    </>
  );
}

function expandAll(groups: PopGroup[], setExpanded: (value: Record<string, boolean>) => void): void {
  const all: Record<string, boolean> = {};
  for (const group of groups) all[group.key] = true;
  setExpanded(all);
}

function PopCard({
  group,
  allVersions,
  membersByHead,
  companies,
  expanded,
  onToggleExpand,
  onToggleCompany,
  grouping,
  onStartGrouping,
  onToggleSelected,
  onCancelGrouping,
  onCreateGroup,
  onUngroup,
  saving,
}: {
  group: PopGroup;
  allVersions: DrawingRow[];
  membersByHead: Map<string, string[]>;
  companies: AudienceCompany[];
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleCompany: (version: DrawingRow, companyId: string) => void;
  grouping: GroupingDraft | null;
  onStartGrouping: () => void;
  onToggleSelected: (projectname: string) => void;
  onCancelGrouping: () => void;
  onCreateGroup: (projectnames: string[]) => void;
  onUngroup: (projectname: string, isHead: boolean) => void;
  saving: boolean;
}) {
  const multi = group.versions.length > 1;
  const groupCount = group.versions.filter((v) => membersByHead.has(v.projectname)).length;

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={onToggleExpand}
        className="pop-head flex w-full flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-left"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
        )}
        {group.pop !== null ? (
          <span className="mono text-sm font-bold text-slate-900">{group.pop}</span>
        ) : (
          // ⚠ Said on the card, not left for somebody to wonder about. DMP has no network row
          // for any of these connections, so it gives them no POP — and the report query
          // reaches an order through that same network row, so none of their orders can
          // appear in Order Activation until DMP places them. Never a POP guessed from the
          // name (hard rule 2).
          <span className="flex items-baseline gap-2">
            <span className="text-sm font-bold text-slate-900">No POP in DMP</span>
            <span className="text-xs" style={{ color: "#b45309" }}>
              DMP has no network row for these connections yet, so their orders cannot reach the report
            </span>
          </span>
        )}
        {multi ? (
          <span className="pill pill-amber">{group.versions.length} versions</span>
        ) : (
          <span className="pill pill-slate">1 version</span>
        )}
        {groupCount > 0 && (
          <span className="pill pill-slate">
            {groupCount} group{groupCount === 1 ? "" : "s"}
          </span>
        )}
        {group.newCount > 0 && <span className="pill pill-rose">{group.newCount} new</span>}
        <span className="ml-auto text-xs text-slate-500">
          <span className="num font-semibold text-slate-700">{group.openCount}</span> of {group.versions.length} open ·{" "}
          <span className="num font-semibold text-slate-700">{formatNumber(group.orders)}</span> orders
        </span>
      </button>

      {expanded && (
        <div>
          {group.versions.map((version) => {
            const members = membersByHead.get(version.projectname);
            const role: VersionRole = members ? "head" : version.groupHead ? "member" : "single";
            // ⚠ Already-grouped drawings cannot be ticked into a new group: moving one would
            // silently change the audience it follows. The reason is on the disabled box.
            const lockedReason =
              role === "member"
                ? `Already grouped under ${version.groupHead} — take it out of that group first`
                : role === "head"
                  ? "Already heads a group — dissolve it first to regroup these drawings"
                  : null;
            return (
              <VersionRow
                key={version.projectname}
                version={version}
                role={role}
                memberCount={members?.length ?? 0}
                companies={companies}
                onToggleCompany={(companyId) => onToggleCompany(version, companyId)}
                onUngroup={() => onUngroup(version.projectname, role === "head")}
                selectable={
                  grouping
                    ? {
                        checked: grouping.names.includes(version.projectname),
                        disabledReason: lockedReason,
                        onToggle: () => onToggleSelected(version.projectname),
                      }
                    : null
                }
                saving={saving}
              />
            );
          })}

          {grouping ? (
            <GroupingPanel
              draft={grouping}
              allVersions={allVersions}
              companies={companies}
              saving={saving}
              onCancel={onCancelGrouping}
              onCreate={onCreateGroup}
            />
          ) : (
            multi && (
              <div className="flex items-center border-t border-slate-100 px-4 py-2">
                <button type="button" className="btn btn-sm btn-ghost" disabled={saving} onClick={onStartGrouping}>
                  <Layers className="h-3.5 w-3.5" /> Group merged drawings…
                </button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Composing a group on one POP: the head it will get, and exactly what adopting the head's
 * audience does before the click.
 *
 * ⚠ Members take the HEAD's audience, so grouping can publish or hide orders. The figures
 * come from `impactOfChanges` — the same arithmetic the server uses and reports — with
 * gains and losses stated apart, because one grouping can do both to one company (hard
 * rules 23 and 24).
 */
function GroupingPanel({
  draft,
  allVersions,
  companies,
  saving,
  onCancel,
  onCreate,
}: {
  draft: GroupingDraft;
  allVersions: DrawingRow[];
  companies: AudienceCompany[];
  saving: boolean;
  onCancel: () => void;
  onCreate: (projectnames: string[]) => void;
}) {
  const selected = allVersions.filter((v) => draft.names.includes(v.projectname));
  const problem = selected.length >= 2 ? groupingProblem(selected, allVersions) : null;
  const headName = groupHeadOf(draft.names);
  const head = allVersions.find((v) => v.projectname === headName) ?? null;
  const members = selected.filter((v) => v.projectname !== headName);

  const impact = head
    ? impactOfChanges(
        members.map((m) => ({
          projectname: m.projectname,
          before: m.openToCompanyIds,
          after: head.openToCompanyIds,
          ordersByContractor: m.ordersByContractor,
        })),
        companies,
      )
    : { names: [], gains: {}, losses: {} };

  const headAudience = head
    ? companies.filter((c) => head.openToCompanyIds.includes(c.id)).map((c) => c.name)
    : [];
  const ready = selected.length >= 2 && !problem && head !== null;

  return (
    <div className="space-y-2 border-t border-slate-200 px-4 py-3" style={{ borderLeft: "4px solid #f59e0b" }}>
      <p className="text-sm font-semibold text-slate-800">Group merged drawings</p>

      {selected.length < 2 ? (
        <p className="text-xs text-slate-500">
          Tick the drawings that were merged. The lowest-numbered becomes the group&rsquo;s name, and the others are
          listed underneath it and opened and hidden with it.
        </p>
      ) : problem ? (
        <p className="text-xs" style={{ color: "#b45309" }}>
          ⚠ {problem}
        </p>
      ) : (
        <>
          <p className="text-sm text-slate-700">
            Group under <span className="mono font-semibold">{headName}</span>:{" "}
            <span className="mono">{members.map((m) => m.projectname).join(", ")}</span>{" "}
            {members.length === 1 ? "follows" : "follow"} its audience —{" "}
            {headAudience.length === 0 ? (
              <span className="font-semibold">hidden from every contractor</span>
            ) : (
              <>
                open to <span className="font-semibold">{headAudience.join(" and ")}</span>
              </>
            )}
            .
          </p>
          {impact.names.length === 0 ? (
            <p className="text-xs text-slate-500">
              Nothing opens or closes for anyone — {members.length === 1 ? "it already has" : "they already have"}{" "}
              that audience.
            </p>
          ) : (
            <>
              {Object.keys(impact.gains).length > 0 && (
                <p className="text-sm text-slate-700">
                  {phraseImpact(companies, impact.gains, "gets", "more open orders")} immediately.
                </p>
              )}
              {Object.keys(impact.losses).length > 0 && (
                <p className="text-sm" style={{ color: "#b45309" }}>
                  ⚠ {phraseImpact(companies, impact.losses, "loses", "open orders")} immediately.
                </p>
              )}
            </>
          )}
        </>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button type="button" className="btn btn-sm btn-outline" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-sm gradient-btn"
          disabled={saving || !ready}
          onClick={() => onCreate(draft.names)}
        >
          {ready ? `Group under ${headName}` : "Group them"}
        </button>
      </div>
    </div>
  );
}

type VersionRole = "head" | "member" | "single";

/**
 * One drawing version.
 *
 * ⚠ The mockup's single tick is now one tick PER CONTRACTOR, because a drawing is
 * published to an audience rather than switched on. Ticking both opens it to each — and
 * each sees only the orders DMP assigns to them (26 Oct 2026).
 *
 * The DMP contractors already present in the drawing are shown beside the ticks, because
 * that is the information the decision turns on — publishing to a company with no rows in
 * a drawing publishes nothing to them, and Ubi should be able to see that before clicking.
 */
function VersionRow({
  version,
  role,
  memberCount,
  companies,
  onToggleCompany,
  onUngroup,
  selectable,
  saving,
}: {
  version: DrawingRow;
  /** Head of a group (tinted, carries the ticks), a member (indented, follows), or neither. */
  role: VersionRole;
  memberCount: number;
  companies: AudienceCompany[];
  onToggleCompany: (companyId: string) => void;
  onUngroup: () => void;
  /** Present while a group is being composed on this POP. */
  selectable: { checked: boolean; disabledReason: string | null; onToggle: () => void } | null;
  saving: boolean;
}) {
  const open = isDrawingOpen(version);
  const member = role === "member";
  // ⚠ Counts, not just names. "Circet 4 · Constructel 4" is the information the decision
  // turns on — publishing to a company with no rows in a drawing publishes nothing to
  // them, and the bare list of names did not say so.
  const breakdown = companies
    .map((c) => ({ name: c.name, n: ordersVisibleTo(version, c.dmpCodes) }))
    .filter((c) => c.n > 0);
  const contractors = breakdown.length
    ? breakdown.map((c) => `${c.name} ${c.n}`).join(" · ")
    : // Em dash, never "N/A" and never blank. A drawing whose every order is unassigned is
      // a real case — 20 such rows in production — and it is worth seeing.
      "—";
  const audienceNames = companies.filter((c) => version.openToCompanyIds.includes(c.id)).map((c) => c.name);

  return (
    <div
      className={`ver-row flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5${open ? "" : " is-hidden-drawing"}`}
      style={role === "head" ? HEAD_ROW_STYLE : member ? MEMBER_ROW_STYLE : undefined}
    >
      <span className={`flex min-w-[220px] flex-1 items-center gap-3${member ? " pl-6" : ""}`}>
        {selectable && (
          <input
            type="checkbox"
            className="tick"
            checked={selectable.checked}
            disabled={selectable.disabledReason !== null}
            title={selectable.disabledReason ?? `Include ${version.projectname} in the group`}
            aria-label={`Include ${version.projectname} in the group`}
            onChange={selectable.onToggle}
          />
        )}
        {member && <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-slate-300" aria-hidden />}
        {/* ⚠ nowrap, and "follows" on its own line beneath: a drawing name breaks at its
            hyphens, and a member row squeezed by the indent read as four ragged lines. */}
        <span className="flex flex-col">
          <span
            className={`mono whitespace-nowrap text-sm font-semibold ${
              member ? "text-slate-500" : open ? "text-slate-800" : "text-slate-400"
            }`}
          >
            {version.projectname}
          </span>
          {member && (
            <span className="whitespace-nowrap text-[11px] leading-tight text-slate-400">
              follows <span className="mono">{version.groupHead}</span>
            </span>
          )}
        </span>
        {role === "head" && (
          <span className="pill pill-amber" title="Merged drawings, opened and hidden as one unit">
            Group of {memberCount + 1}
          </span>
        )}
        {version.isNew && <span className="pill pill-rose">New</span>}
        {isIrregularDrawingName(version.projectname) && (
          <span className="pill pill-amber" title="Does not follow the POP-version naming convention">
            Irregular
          </span>
        )}
      </span>

      <span className={`w-24 shrink-0 whitespace-nowrap text-right text-sm ${open ? "text-slate-700" : "text-slate-400"}`}>
        <span className="num inline-block w-8 text-right">{formatNumber(version.orders)}</span>{" "}
        <span className="text-xs text-slate-400">orders</span>
      </span>

      {/* ⚠ Sized by measurement, not by eye: "Circet 26 · Constructel 38" is 149 px in
          Montserrat 12 px and the column was 144, so Ubi saw "Constructel …". The widest
          realistic value, three digits each, is 164 px; w-48 is 192. Truncation stays as
          the fallback for a third contractor, with the full text on hover. */}
      <span
        className="w-48 shrink-0 truncate text-xs text-slate-500"
        title={`DMP assigns these orders to: ${contractors}. Orders with no contractor are open to nobody.`}
      >
        {contractors}
      </span>

      <span className="flex w-32 shrink-0 justify-end">
        {version.oldestAgeDays === null ? (
          <span className="pill pill-slate">No orders</span>
        ) : (
          <span
            className={`pill pill-num pill-${ageTone(version.oldestAgeDays)}`}
            title="Age of the oldest order in this drawing"
          >
            Oldest {version.oldestAgeDays} days
          </span>
        )}
      </span>

      {/* The control itself: published to whom. ⚠ A member's ticks follow its head —
          disabled here, because a group is published as one unit and the head's ticks are
          the only ones that act on it. The server would widen a member's tick anyway. */}
      <span className="flex shrink-0 items-center gap-3">
        {companies.map((company) => {
          const ticked = version.openToCompanyIds.includes(company.id);
          const wouldSee = ordersVisibleTo(version, company.dmpCodes);
          return (
            <label
              key={company.id}
              className={`flex items-center gap-1.5 text-xs ${member ? "cursor-not-allowed" : "cursor-pointer"}`}
              title={
                member
                  ? `follows ${version.groupHead}`
                  : role === "head"
                    ? `Open the whole group (${memberCount + 1} drawings) to ${company.name}`
                    : wouldSee > 0
                      ? `Open this drawing to ${company.name} — ${wouldSee} of their own orders`
                      : `${company.name} has no orders of their own in this drawing; opening it to them opens nothing to them`
              }
            >
              <input
                type="checkbox"
                className="tick"
                checked={ticked}
                disabled={saving || member}
                onChange={() => onToggleCompany(company.id)}
              />
              <span className={ticked ? "font-semibold text-slate-700" : "text-slate-400"}>{company.name}</span>
            </label>
          );
        })}
      </span>

      <span className="w-28 shrink-0 text-right">
        {audienceNames.length === 0 ? (
          <span className="pill pill-slate">Hidden</span>
        ) : audienceNames.length === 1 ? (
          <span className="pill pill-emerald">Open</span>
        ) : (
          // Its own pill, so a drawing worked by both reads at a glance. Since 26 Oct 2026
          // neither company sees the other's orders in it.
          <span
            className="pill pill-violet"
            title={`Open to ${audienceNames.join(" and ")} — each sees only their own orders`}
          >
            Shared
          </span>
        )}
      </span>

      {/* Ungrouping moves no audience, so it acts at once — it only changes what the next
          tick lands on. */}
      <span className="flex w-24 shrink-0 justify-end">
        {role === "head" && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            disabled={saving}
            title="Dissolve the group — every drawing keeps the audience it has now"
            onClick={onUngroup}
          >
            Ungroup
          </button>
        )}
        {member && (
          <button
            type="button"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            disabled={saving}
            title={`Take ${version.projectname} out of the group — it keeps the audience it has now`}
            aria-label={`Take ${version.projectname} out of the group`}
            onClick={onUngroup}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </div>
  );
}
